import { PropsWithChildren } from 'react';

import { AssetClass, AssetType, PutCall, SubAssetType } from '../../_openapi/portfolio-optimizer';
import { FormDatePicker } from '../../widgets/Form/FormDatePicker';
import { FormInput } from '../../widgets/Form/FormInput';
import { FormNumberInput } from '../../widgets/Form/FormNumberInput';
import { FormRadioGroup } from '../../widgets/Form/FormRadioGroup';
import { FormSelect } from '../../widgets/Form/FormSelect';

const LABEL_WIDTH = '240px';

const fieldSettings = {
    labelWidth: LABEL_WIDTH,
    size: 'sm',
};

const Multipliers = () => (
    <>
        <FormNumberInput
            label="Price Multiplier"
            fieldName="priceMultiplier"
            step={0.01}
            precision={2}
            {...fieldSettings}
            isRequired
            isReadOnly
        />
        <FormNumberInput
            label="Contract Unit"
            fieldName="contractMultiplier"
            step={0.01}
            precision={2}
            {...fieldSettings}
            isRequired
            isReadOnly
        />
    </>
);

const BondFields = () => (
    <>
        <DescriptionField />
        <AssetTypeField>
            <option value={AssetType.MUNI}>Muni</option>
            <option value={AssetType.TREASURY}>Treasury</option>
        </AssetTypeField>
        <SubAssetTypeField>
            <option value={SubAssetType.BILL}>Bill</option>
            <option value={SubAssetType.BOND}>Bond</option>
            <option value={SubAssetType.NOTE}>Note</option>
        </SubAssetTypeField>
        <CusipField />
        <FormInput label="Issuer" fieldName="issuer" {...fieldSettings} isReadOnly />
        <FormNumberInput
            label="Coupon Rate"
            fieldName="coupon"
            {...fieldSettings}
            step={0.01}
            precision={2}
            isReadOnly
        />
        <FormDatePicker label="Maturity Date" fieldName="maturityDate" {...fieldSettings} isRequired isReadOnly />
        <FormDatePicker label="Issue Date" fieldName="issueDate" {...fieldSettings} isReadOnly />
        <FormNumberInput label="Years to Maturity" fieldName="length" {...fieldSettings} isReadOnly />
        <Multipliers />
    </>
);

const EquityFields = () => (
    <>
        <DescriptionField />
        <AssetTypeField>
            <option value={AssetType.ETF}>ETF</option>
            <option value={AssetType.ADR}>ADR</option>
            <option value={AssetType.PREFERRED}>Preferred</option>
            <option value={AssetType.SPAC}>SPAC</option>
            <option value={AssetType.COMMON_STOCK}>Common Stock</option>
            <option value={AssetType.WARRANT}>Warrant</option>
            <option value={AssetType.MUTUAL_FUND}>Mutual Fund</option>
        </AssetTypeField>
        <SubAssetTypeField>
            <option value={SubAssetType.ETF}>ETF</option>
            <option value={SubAssetType.ETN}>ETN</option>
            <option value={SubAssetType.ETV}>ETV</option>
        </SubAssetTypeField>
        <FormInput label="Share Class ID" fieldName="scId" {...fieldSettings} isReadOnly />
        <FormInput label="Company ID" fieldName="coId" {...fieldSettings} isReadOnly />
        <FormInput label="Symbol" fieldName="symbol" {...fieldSettings} isRequired isReadOnly />
        <CusipField />
        <FormInput label="Ticker" fieldName="ticker" {...fieldSettings} isRequired isReadOnly />
        <FormInput label="ISIN" fieldName="isin" {...fieldSettings} isRequired isReadOnly />
        <FormInput label="Sedol" fieldName="sedol" {...fieldSettings} isReadOnly />
        <ExchangeField />
        <FormInput label="Domicile Country" fieldName="country" {...fieldSettings} isReadOnly />
        <FormInput label="Market ID Code" fieldName="mic" {...fieldSettings} isReadOnly />
        <FormInput
            label="Global Industry Classification Code"
            fieldName="gicsCode"
            {...fieldSettings}
            isRequired
            isReadOnly
        />
        <FormInput label="Industry Sector" fieldName="gicsSector" {...fieldSettings} isRequired isReadOnly />
        <FormInput label="Industry" fieldName="gicsIndustry" {...fieldSettings} isRequired isReadOnly />
        <Multipliers />
    </>
);

const FuturesFields = () => (
    <>
        <DescriptionField />
        <AssetTypeField>
            <option value={AssetType.INTEREST_RATES}>Interest Rates</option>
            <option value={AssetType.VOLATILITY}>Volatility</option>
        </AssetTypeField>
        <FormInput label="NDVR Chain ID" fieldName="ndvrChainId" {...fieldSettings} isRequired isReadOnly />
        <FormInput label="Symbol" fieldName="symbol" {...fieldSettings} isRequired isReadOnly />
        <UnderlyingSymbolField />
        <ExpirationDateField />
        <FormDatePicker label="First Notice Date" fieldName="firstNoticeDate" {...fieldSettings} isReadOnly />
        <ExchangeField />
        <FormInput
            label="Exchange Product Code"
            fieldName="exchangeProductCode"
            {...fieldSettings}
            isRequired
            isReadOnly
        />
        <FormNumberInput
            label="Minimum Tick Size"
            fieldName="minTick"
            {...fieldSettings}
            step={0.01}
            precision={2}
            isReadOnly
        />
        <FormInput label="Settlement Method" fieldName="settlementMethod" {...fieldSettings} isReadOnly />
        <FormInput label="Futures Sector" fieldName="market" {...fieldSettings} isReadOnly />
        <Multipliers />
    </>
);

const OptionsFields = () => (
    <>
        <DescriptionField />
        <AssetTypeField>
            <option value={AssetType.FUTURES}>Futures</option>
            <option value={AssetType.INDEX}>Index</option>
        </AssetTypeField>
        <UnderlyingSymbolField />
        <ExpirationDateField />
        <FormNumberInput
            label="Strike Price"
            fieldName="strikePrice"
            {...fieldSettings}
            step={0.01}
            precision={2}
            isReadOnly
        />
        <FormRadioGroup
            label="Option Type"
            fieldName="putCall"
            labelWidth={LABEL_WIDTH}
            options={[
                { label: 'Put', value: PutCall.P },
                { label: 'Call', value: PutCall.C },
            ]}
            isReadOnly
        />
        <Multipliers />
    </>
);

const OtherFields = () => <DescriptionField />;

export const AssetFormFields: { [k in AssetClass]: () => JSX.Element } = {
    Bond: BondFields,
    Equity: EquityFields,
    Futures: FuturesFields,
    Options: OptionsFields,
    Other: OtherFields,
};

const AssetTypeField = ({ children }: PropsWithChildren) => (
    <FormSelect label="Asset Type" fieldName="assetType" {...fieldSettings} isRequired isReadOnly>
        <option></option>
        {children}
    </FormSelect>
);
const SubAssetTypeField = ({ children }: PropsWithChildren) => (
    <FormSelect label="Sub Asset Type" fieldName="subAssetType" {...fieldSettings} isReadOnly>
        <option></option>
        {children}
    </FormSelect>
);
const DescriptionField = () => (
    <FormInput label="Security Description" fieldName="description" {...fieldSettings} isRequired isReadOnly />
);
const CusipField = () => <FormInput label="Cusip" fieldName="cusip" {...fieldSettings} isRequired isReadOnly />;
const ExchangeField = () => (
    <FormInput label="Exchange" fieldName="primaryExchange" {...fieldSettings} isRequired isReadOnly />
);
const ExpirationDateField = () => (
    <FormDatePicker label="Expiration Date" fieldName="expirationDate" {...fieldSettings} isRequired isReadOnly />
);
const UnderlyingSymbolField = () => (
    <FormInput label="Underlying Symbol" fieldName="underlyingSymbol" {...fieldSettings} isRequired isReadOnly />
);
